import { SagaIterator } from 'redux-saga';
import { call, race, select, take, takeEvery } from 'redux-saga/effects';
import {
  BeginnAblaufGeaendert,
  GlobalActions,
  GlobalPut,
  SOFORTSCHUTZ_ACTIVE_REQUEST,
} from '@ps20-frontend/shop-integration-redux';
import dayjs from 'dayjs';
import { App } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_LOCAL_STATE } from '@ps20-frontend/utils';
import { IAppState } from '../localStore';
import { selectProdukt } from '../selector/produkt.selector';
import {
  getAeltestenVersicherungsbeginn,
  isVertragslaufzeitInAllenProdukten3Jahre,
  LAUFZEIT_3_JAHRE,
} from './beginnAblaufSaga';

export function* watchUpdateAbgelaufenerVersicherungsbeginn(): SagaIterator {
  yield takeEvery(
    GlobalActions.UPDATE_ABGELAUFENER_VERSICHERUNGSBEGINN,
    safeSaga(updateAbgelaufeneVersicherungsbeginn, App.SHOP_CHECKOUT),
  );
}

function* updateAbgelaufeneVersicherungsbeginn(): SagaIterator {
  const state: IAppState = yield select();
  const abgelaufeneProdukte = state.produkte.filter(produkt =>
    isBeginndatumAbgelaufen(produkt.beginn),
  );
  const globalPut = GlobalPut.getInstance();

  if (abgelaufeneProdukte.length > 0) {
    const updateBeginnAblaufPayloads: BeginnAblaufGeaendert[] = yield call(
      generateBeginnAblaufPayloads,
    );
    yield call(
      globalPut.put,
      new GlobalActions().updateBeginnAblaufOfProdukte(updateBeginnAblaufPayloads),
    );

    for (const produkt of abgelaufeneProdukte) {
      const currentState: IAppState = yield select();
      if (!selectProdukt(currentState, produkt)) {
        continue;
      }
      yield call(
        globalPut.put,
        new GlobalActions().updateSofortschutzActive(produkt.appId, produkt.angebotsId),
      );
      const { failed } = yield race({
        success: take(GlobalActions.UPDATE_SOFORTSCHUTZ_ACTIVE_FINISHED),
        failed: take(SOFORTSCHUTZ_ACTIVE_REQUEST.SOFORTSCHUTZ_ACTIVE_REQUEST_FAILED),
      });
      if (failed) {
        yield call(globalPut.put, new GlobalActions().updateAbgelaufeneVersicherungsbeginnFailed());
        return yield call(globalPut.put, new GlobalActions().errorPageRequested());
      }
    }
  }
  yield call(globalPut.put, new GlobalActions().updateAbgelaufeneVersicherungsbeginnSuccessfull());
}

function* generateBeginnAblaufPayloads(): SagaIterator {
  const minBeginn: string = getMinBeginndatum().format(DATEFORMAT_LOCAL_STATE);
  const state: IAppState = yield select();
  if (isVertragslaufzeitInAllenProdukten3Jahre(state.produkte)) {
    const updatedBeginnPayload = state.produkte.map(produkt => ({
      appId: produkt.appId,
      angebotsId: produkt.angebotsId,
      beginn: isBeginndatumAbgelaufen(produkt.beginn) ? minBeginn : produkt.beginn,
      ablauf: produkt.ablauf,
    }));
    const aeltesterVersicherungsbeginn = getAeltestenVersicherungsbeginn(
      updatedBeginnPayload.map(payload => payload.beginn),
    );
    return updatedBeginnPayload.map(payload => ({
      ...payload,
      ablauf: aeltesterVersicherungsbeginn
        .add(LAUFZEIT_3_JAHRE, 'year')
        .format(DATEFORMAT_LOCAL_STATE),
    }));
  } else {
    return state.produkte.map(produkt => {
      const beginn: string = isBeginndatumAbgelaufen(produkt.beginn) ? minBeginn : produkt.beginn;
      return {
        appId: produkt.appId,
        angebotsId: produkt.angebotsId,
        beginn,
        ablauf: dayjs(beginn, DATEFORMAT_LOCAL_STATE)
          .add(produkt.laufzeit, 'year')
          .format(DATEFORMAT_LOCAL_STATE),
      };
    });
  }
}

export const getMinBeginndatum = function () {
  return dayjs().startOf('day').add(1, 'd');
};

export function isBeginndatumAbgelaufen(beginn: string): boolean {
  const startMin = getMinBeginndatum();
  return dayjs(beginn, DATEFORMAT_LOCAL_STATE).isBefore(startMin);
}
